import React, { useMemo, useState } from 'react';
import { ChallengeApiError, ChallengeError, useChallengeSecurityKey } from '../../hooks';
import messages from './MfaChallengeSecurityKey.messages';
import {
  InlineMessageContainer,
  InputMessage,
  StyledMessage,
} from '../../containers/MultiFactorPage.helpers';
import InlineMessage from '@atlaskit/inline-message';
import SectionMessage from '@atlaskit/section-message';
import TallButton from '../TallButton';
import { SecurityKey } from '../SecurityKey';
import { FormattedMessage, useIntl } from 'react-intl';
import { token } from '@atlaskit/tokens';
import styled from '@emotion/styled';
import { WebAuthnAuthenticationErrorCode } from '../../helpers/webauthn';
import { useNavigateSoftOrHard } from '../../utilities/hooks/useNavigateSoftOrHard';

const baseSpacing = token('space.100');
const PAGE_ID = 'MfaChallengeSecurityKey';

const ErrorMessageWrapper = styled.div`
  margin: calc(${baseSpacing} * 2) 0;
`;
const MfaChallengeSecurityKey = () => {
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const { formatMessage } = useIntl();

  const challengeErrorMessages = useMemo(
    (): Record<ChallengeError, string> => ({
      // WebAuthn errors
      [WebAuthnAuthenticationErrorCode.WebAuthnNotSupported]: formatMessage(
        messages.webauthnNotSupportedMessage
      ),
      [WebAuthnAuthenticationErrorCode.InvalidRequestOptions]: formatMessage(
        messages.webAuthnMalformedConfiguration // server returned bad options?
      ),
      [WebAuthnAuthenticationErrorCode.Aborted]: formatMessage(messages.webAuthnAborted),
      [WebAuthnAuthenticationErrorCode.NotAllowed]: formatMessage(
        messages.webAuthnAuthenticationIncomplete // eg. timeout, user cancelled
      ),
      [WebAuthnAuthenticationErrorCode.InvalidDomain]: formatMessage(
        messages.webAuthnInvalidDomain
      ),
      [WebAuthnAuthenticationErrorCode.InvalidRpId]: formatMessage(
        messages.webAuthnMalformedConfiguration // server returned bad options?
      ),
      [WebAuthnAuthenticationErrorCode.UnknownError]: formatMessage(
        messages.webauthnAuthenticationFailed
      ),
      // API errors
      [ChallengeApiError.tooManyRequests]: formatMessage(
        messages.securityKeyTooManyAttemptsMessage
      ),
      [ChallengeApiError.transactionTokenNotFound]: formatMessage(messages.failedToUseKeyMessage),
      [ChallengeApiError.challengeFailure]: formatMessage(messages.failedToUseKeyMessage),
    }),
    [formatMessage]
  );

  const navigate = useNavigateSoftOrHard();
  const { challengeSecurityKey, authenticating } = useChallengeSecurityKey(
    PAGE_ID,
    responseData => {
      const redirectUri = responseData.redirectUri;
      navigate(redirectUri.toString());
    },
    error => {
      setErrorMessage(
        challengeErrorMessages[error] || formatMessage(messages.failedToUseKeyMessage)
      );
    }
  );

  const invokeChallengeSecurityKey = () => {
    setErrorMessage(undefined);
    challengeSecurityKey();
  };

  return (
    <SecurityKey>
      <InputMessage style={{ margin: `${baseSpacing} 0` }}>
        <FormattedMessage
          id="mf.mfform.securitykey.authenticate"
          defaultMessage="Use your security key to continue."
        />
        <InlineMessageContainer data-testid="mfform-tooltip-icon">
          <InlineMessage appearance="info" placement="bottom-end">
            <StyledMessage>
              <FormattedMessage
                id="mf.mfform.securitykey.tooltip"
                defaultMessage="Use any security key you registered with Atlassian"
              />
            </StyledMessage>
          </InlineMessage>
        </InlineMessageContainer>
      </InputMessage>
      {errorMessage && (
        <ErrorMessageWrapper>
          <SectionMessage appearance="warning">{errorMessage}</SectionMessage>
        </ErrorMessageWrapper>
      )}
      <TallButton
        id="challenge-security-key"
        appearance="primary"
        isFullWidth
        onClick={invokeChallengeSecurityKey}
        isLoading={authenticating}>
        <FormattedMessage id="mf.mfform.securitykey" defaultMessage="Log in" />
      </TallButton>
    </SecurityKey>
  );
};

export default MfaChallengeSecurityKey;
